import { ColumnFilter, ColumnFilterMap, FilterOption } from 'util/filtering/models';

export interface CardGroupFilterConfig {
  key: string;
  backendField: string;
  type: 'string' | 'enum' | 'exists';
  name: string;
  supportsExclude?: boolean;
  staticOptions?: FilterOption[];
  isDynamic?: boolean;
  transform?: (values: FilterOption[], rule: string) => Record<string, unknown>[];
}

export interface CardGroupDynamicFilterOptions {
  projectName?: FilterOption[];
  glCode?: FilterOption[];
  groupAdmin?: FilterOption[];
  cardholder?: FilterOption[];
  last4?: FilterOption[];
}

export interface CardGroupFilterBuildResult {
  andConditions: Array<{ or: Array<Record<string, unknown>> }>;
}

export const CARD_GROUP_FILTERS: Record<string, CardGroupFilterConfig> = {
  adminStatus: {
    key: 'adminStatus',
    backendField: 'hasGroupAdmin',
    type: 'exists',
    name: 'Admin status',
    staticOptions: [
      { id: 'assigned', label: 'Group admin assigned' },
      { id: 'not_assigned', label: 'No group admin assigned' },
    ],
    transform: (values: FilterOption[], rule: string) => {
      // For "allExcept", we want to EXCLUDE the selected values
      // So we need to find what's NOT selected and include those
      if (rule === 'allExcept') {
        const allOptions = ['assigned', 'not_assigned'];
        const selectedIds = new Set(values.map((v) => v.id));
        const remainingOptions = allOptions.filter((id) => !selectedIds.has(id));

        return remainingOptions.map((id) => {
          if (id === 'assigned') {
            return { hasGroupAdmin: { eq: true } };
          }
          if (id === 'not_assigned') {
            return { hasGroupAdmin: { eq: false } };
          }
          return {};
        });
      }

      // For "noExcept" (default), include the selected values
      return values.map((v) => {
        if (v.id === 'assigned') {
          return { hasGroupAdmin: { eq: true } };
        }
        if (v.id === 'not_assigned') {
          return { hasGroupAdmin: { eq: false } };
        }
        return {};
      });
    },
  },
  spendPolicy: {
    key: 'spendPolicy',
    backendField: 'isMonthlyBudgetUnlimited',
    type: 'exists',
    name: 'Spend policy',
    staticOptions: [
      { id: 'unlimited', label: 'Unlimited spending' },
      { id: 'capped', label: 'Capped (limit set)' },
    ],
    transform: (values: FilterOption[], rule: string) => {
      // For "allExcept", we want to EXCLUDE the selected values
      if (rule === 'allExcept') {
        const allOptions = ['unlimited', 'capped'];
        const selectedIds = new Set(values.map((v) => v.id));
        const remainingOptions = allOptions.filter((id) => !selectedIds.has(id));

        return remainingOptions.map((id) => {
          if (id === 'unlimited') {
            return { isMonthlyBudgetUnlimited: { eq: true } };
          }
          if (id === 'capped') {
            return { isMonthlyBudgetUnlimited: { eq: false } };
          }
          return {};
        });
      }

      // For "noExcept" (default), include the selected values
      return values.map((v) => {
        if (v.id === 'unlimited') {
          return { isMonthlyBudgetUnlimited: { eq: true } };
        }
        if (v.id === 'capped') {
          return { isMonthlyBudgetUnlimited: { eq: false } };
        }
        return {};
      });
    },
  },
  visibilitySettings: {
    key: 'visibilitySettings',
    backendField: 'visibilitySettings',
    type: 'enum',
    name: 'Visibility settings',
    staticOptions: [
      { id: 'full_access', label: 'Full access' },
      { id: 'gl_code_only', label: 'GL code restrictions only' },
      { id: 'project_only', label: 'Project restrictions only' },
      { id: 'gl_code_and_project', label: 'GL code and project restrictions' },
    ],
    transform: (values: FilterOption[], rule: string) => {
      // Map visibility setting IDs to their filter conditions
      const getCondition = (id: string) => {
        switch (id) {
          case 'full_access':
            // Both have full access (isAllAvailable = true)
            return { visibilitySettings: { eq: 'full_access' } };
          case 'gl_code_only':
            // GL code restricted, project full access
            return { visibilitySettings: { eq: 'gl_code_only' } };
          case 'project_only':
            // GL code full access, project restricted
            return { visibilitySettings: { eq: 'project_only' } };
          case 'gl_code_and_project':
            // Both restricted
            return { visibilitySettings: { eq: 'gl_code_and_project' } };
          default:
            return {};
        }
      };

      if (rule === 'allExcept') {
        const allOptions = ['full_access', 'gl_code_only', 'project_only', 'gl_code_and_project'];
        const selectedIds = new Set(values.map((v) => v.id));
        const remainingOptions = allOptions.filter((id) => !selectedIds.has(id));
        return remainingOptions.map((id) => getCondition(id));
      }

      return values.map((v) => getCondition(v.id));
    },
  },
  projectName: {
    key: 'projectName',
    backendField: 'projectVisibilityIds',
    type: 'string',
    name: 'Project name',
    supportsExclude: true,
    isDynamic: true,
    transform: (values: FilterOption[], rule: string) => {
      const operator = rule === 'noExcept' ? 'contains' : 'notContains';
      return values.map((v) => ({
        projectVisibilityIds: { [operator]: v.id },
      }));
    },
  },
  glCode: {
    key: 'glCode',
    backendField: 'glCodeVisibilityIds',
    type: 'string',
    name: 'GL code',
    supportsExclude: true,
    isDynamic: true,
    transform: (values: FilterOption[], rule: string) => {
      const operator = rule === 'noExcept' ? 'contains' : 'notContains';
      return values.map((v) => ({
        glCodeVisibilityIds: { [operator]: v.id },
      }));
    },
  },
  groupAdmin: {
    key: 'groupAdmin',
    backendField: 'groupAdminId',
    type: 'string',
    name: 'Group admin',
    supportsExclude: true,
    isDynamic: true,
    transform: (values: FilterOption[], rule: string) => {
      const operator = rule === 'noExcept' ? 'contains' : 'notContains';
      return values.map((v) => ({
        groupAdminId: { [operator]: v.id },
      }));
    },
  },
  cardholder: {
    key: 'cardholder',
    backendField: 'cardholderId',
    type: 'string',
    name: 'Cardholder',
    supportsExclude: true,
    isDynamic: true,
    transform: (values: FilterOption[], rule: string) => {
      const operator = rule === 'noExcept' ? 'contains' : 'notContains';
      return values.map((v) => ({
        cardholderId: { [operator]: v.id },
      }));
    },
  },
  last4: {
    key: 'last4',
    backendField: 'last4',
    type: 'string',
    name: 'Last 4 digits',
    supportsExclude: true,
    isDynamic: true,
    transform: (values: FilterOption[], rule: string) => {
      // Filter: show groups that contain at least one card with any of the selected last4 values
      const operator = rule === 'noExcept' ? 'contains' : 'notContains';
      return values.map((v) => ({
        last4: { [operator]: v.id },
      }));
    },
  },
};

const buildDefaultConditions = (
  filter: ColumnFilter,
  config: CardGroupFilterConfig
): Record<string, unknown>[] => {
  const rule = filter.rule || 'noExcept';
  const operator = rule === 'noExcept' ? 'eq' : 'ne';
  const selectedValues = filter.selectedValues || [];

  return selectedValues.map((value) => ({
    [config.backendField]: { [operator]: value.id },
  }));
};

// Helper to check if a condition uses exclusion operators (notContains, ne)
const isExclusionCondition = (condition: Record<string, unknown>): boolean => {
  return Object.values(condition).some((value) => {
    if (typeof value === 'object' && value !== null) {
      return 'notContains' in value || 'ne' in value;
    }
    return false;
  });
};

export const buildCardGroupFilterQuery = (columnFilters: ColumnFilterMap): CardGroupFilterBuildResult => {
  const andConditions: Array<{ or: Array<Record<string, unknown>> }> = [];

  Object.entries(columnFilters).forEach(([key, filter]) => {
    if (!filter.selectedValues?.length) return;

    const config = CARD_GROUP_FILTERS[key];
    if (!config) return;

    const rule = filter.rule || 'noExcept';
    const conditions = config.transform
      ? config.transform(filter.selectedValues, rule)
      : buildDefaultConditions(filter, config);

    const validConditions = conditions.filter((c) => Object.keys(c).length > 0);
    if (validConditions.length > 0) {
      // Check if conditions use exclusion operators (notContains, ne)
      // Exclusion conditions must be ANDed: (NOT A) AND (NOT B) AND (NOT C)
      // Inclusion conditions should be ORed: A OR B OR C
      const hasExclusionOperator = validConditions.some(isExclusionCondition);

      if (hasExclusionOperator) {
        // Each exclusion condition gets its own OR block so they're ANDed together
        // e.g., { and: [{ or: [{ last4: { notContains: '1234' } }] }, { or: [{ last4: { notContains: '5678' } }] }] }
        validConditions.forEach((condition) => {
          andConditions.push({ or: [condition] });
        });
      } else {
        // Inclusion conditions are ORed within a single block
        // e.g., { and: [{ or: [{ hasGroupAdmin: { eq: true } }, { hasGroupAdmin: { eq: false } }] }] }
        andConditions.push({ or: validConditions });
      }
    }
  });

  return { andConditions };
};

export const generateCardGroupFilterObject = (columnFilters: ColumnFilterMap) => {
  const { andConditions } = buildCardGroupFilterQuery(columnFilters);

  if (andConditions.length === 0) {
    return undefined;
  }

  // AND all filter groups together, with OR within each group
  // e.g., { and: [{ or: [{ hasGroupAdmin: { eq: true } }] }, { or: [{ isMonthlyBudgetUnlimited: { eq: true } }] }] }
  return {
    and: andConditions,
  };
};

const mapConfigTypeToColumnFilterType = (
  configType: CardGroupFilterConfig['type']
): 'string' | 'number' | 'date' => {
  if (configType === 'exists' || configType === 'enum') {
    return 'string';
  }
  return configType;
};

export const buildCardGroupColumnFiltersFromConfig = (
  pageName: string,
  dynamicOptions?: CardGroupDynamicFilterOptions
): ColumnFilterMap => {
  return Object.fromEntries(
    Object.entries(CARD_GROUP_FILTERS).map(([key, config]) => {
      let options: FilterOption[] = config.staticOptions || [];

      // Override with dynamic options if available
      if (config.isDynamic && dynamicOptions) {
        const dynamicOpts = dynamicOptions[key as keyof CardGroupDynamicFilterOptions];
        if (dynamicOpts) {
          options = dynamicOpts;
        }
      }

      return [
        key,
        {
          key: config.key,
          type: mapConfigTypeToColumnFilterType(config.type),
          name: config.name,
          rule: 'noExcept',
          page: pageName,
          options,
          selectedValues: [],
        } as ColumnFilter,
      ];
    })
  );
};
